import React, {
  useState, useEffect, useCallback,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  Modal, Form, Button, Divider, Card, Icon,
} from 'semantic-ui-react';
import {
  useAppSelector,
} from 'store';
import {
  OpenAPI
} from 'simplydo/interfaces';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from 'api';
import { useAppDispatch } from 'store';
import { ThemedButton } from 'components/lib/UI';
import actions from 'actions';
import util from 'utils/utils';

type INewChallengeModalWrapper = {
  newChallengeOpen: boolean,
  closeModal: () => void,
}

const NewModal = ({
  newChallengeOpen, closeModal,
}: INewChallengeModalWrapper) => {
  const [creatingChallengeType, setCreatingChallengeType] = useState<''|'manual'|'genai'>('manual');
  const [name, setName] = useState<string>('');
  const user: OpenAPI.GET<'/users/me'> = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const createChallenge = useCallback(() => {
    api.challenges.create(name, ({ _id }) => {
      dispatch(actions.user.onFollow('challenge', _id));
      history.push(`/challenges/${_id}/settings`);
    }, (err) => toast.error(err.message));
  }, [name, history, dispatch]);

  const handleKeyDown = useCallback((e) => {
    if (e.keyCode === 13 && newChallengeOpen && name !== '') {
      createChallenge();
    }
  }, [createChallenge, name, newChallengeOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  useEffect(() => {
    if (util.organisationFeaturesEnabled(user, ['openAI'])) {
      setCreatingChallengeType('');
    }
  }, [user, newChallengeOpen]);

  const onCloseModal = useCallback(() => {
    setCreatingChallengeType('');
    setName('');
    closeModal();  
  }, [closeModal]);

  return (
    <Modal
      open={newChallengeOpen}
      onOpen={() => setCreatingChallengeType('manual')}
      onClose={onCloseModal}
    >
      <Modal.Header>Create challenge</Modal.Header>
      {!creatingChallengeType
        ? (
          <Modal.Content>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly' }}>
              <Card
                fluid
                header={(
                  <Card.Header>
                    <Icon name='edit' />
                    Manually create a challenge
                  </Card.Header>
                )}
                description='Use this option to create a blank challenge, allowing you to start from scratch for each field.'
                onClick={() => setCreatingChallengeType('manual')}
                style={{ margin: '0 20px 0 0' }}
              />
              <Divider
                vertical
                content='Or'
                style={{ height: '50px' }}
              />
              <Card
                fluid
                header={(
                  <Card.Header>
                    <Icon name='magic' />
                    Generate a challenge using AI
                  </Card.Header>
                )}
                description='SimplyDo will ask a few quick questions in order to create a full challenge, which you can then edit and tweak.'
                onClick={() => history.push('/challenges/new/genai')}
                style={{ margin: '0 0 0 20px' }}
              />
            </div>
          </Modal.Content>
        ) : (
          <>
            {creatingChallengeType === 'manual'
              ? (
                <Modal.Content>
                  <p>What would you like to call your challenge? You can always change this later.</p>
                  <Form style={{ marginBottom: 10 }}>
                    <Form.Input onKeyDown={(e) => handleKeyDown(e)} placeholder='Challenge name...' value={name} onChange={(e) => setName(e.target.value)} />
                  </Form>
                </Modal.Content>
              ) : (
                null
              )
            }
          </>
        )
      }

      <Modal.Actions>
        <Button content={t('generic.cancel')} onClick={() => onCloseModal()} />
        {creatingChallengeType === 'manual'
          && (
            <ThemedButton icon='check' content='Create challenge' primary onClick={() => createChallenge()} />
          )
        }
      </Modal.Actions>
    </Modal>
  );
};

export default NewModal;
